import { Input } from "antd";
import React, { useEffect, useState } from "react";
import Slider from "react-slick";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import apiInstance from "../../../utility/apiInstance";

const maskEmail = (email: string): string => {
    const [local, domain] = email.split("@");
    if (!domain) return email;
    const masked = local[0] + "***";
    return `${masked}@${domain}`;
};

const Otp = () => {
    const loginSLider = {
        dots: true,
        infinite: true,
        slidesToShow: 1,
        slidesToScroll: 1,
        adaptiveHeight: true,
    };

    const route = all_routes;
    const navigate = useNavigate();
    const location = useLocation();

    const email: string = (location.state as any)?.email;

    useEffect(() => {
        if (!email) {
            navigate(route.register);
        }
    }, [email, navigate, route.register]);

    const [otp, setOtp] = useState<string>("");
    const [otpInvalid, setOtpInvalid] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [seconds, setSeconds] = useState<number>(60);
    const [timerActive, setTimerActive] = useState<boolean>(true);

    useEffect(() => {
        if (!timerActive) return;
        if (seconds <= 0) {
            setTimerActive(false);
            return;
        }
        const intervalId = setInterval(() => {
            setSeconds((prev) => prev - 1);
        }, 1000);
        return () => clearInterval(intervalId);
    }, [seconds, timerActive]);

    const formatTime = (time: number) => {
        return time < 10 ? `0${time}` : time;
    };

    const handleVerify = async (event: React.FormEvent) => {
        event.preventDefault();
        setError(null);

        if (!otp || otp.length < 4) {
            setError("Please enter the 4-digit OTP.");
            setOtpInvalid(true);
            return;
        }
        setOtpInvalid(false);

        setLoading(true);
        try {
            await apiInstance.post("/api/users/verify-otp", { email, otp });
            navigate(route.login, { state: { verified: true } });
        } catch (err: any) {
            const data = err?.response?.data;
            if (data?.remainingAttempts !== undefined) {
                setError(
                    `${data.error}. ${data.remainingAttempts} attempt${data.remainingAttempts === 1 ? "" : "s"} remaining.`,
                );
            } else {
                setError(data?.error || "OTP verification failed. Please try again.");
            }
        } finally {
            setLoading(false);
        }
    };

    const handleResend = async () => {
        setError(null);
        setSuccessMessage(null);
        setLoading(true);
        try {
            await apiInstance.post("/api/users/send-otp", { email });
            setSeconds(60);
            setTimerActive(true);
            setSuccessMessage("A new OTP has been sent to your email.");
        } catch (err: any) {
            const data = err?.response?.data;
            setError(data?.error || "Failed to resend OTP. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    if (!email) return null;

    return (
        <>
            {/* Main Wrapper */}
            <div className="main-wrapper">
                <div className="login-content">
                    <div className="row">
                        {/* Login Banner */}
                        <div className="col-md-6 login-bg d-none d-lg-flex">
                            <Slider {...loginSLider} className="login-carousel">
                                <div>
                                    <div className="login-carousel-section mb-3">
                                        <div className="login-banner">
                                            <ImageWithBasePath
                                                src="assets/img/auth/auth-1.svg"
                                                className="img-fluid"
                                                alt="Logo"
                                            />
                                        </div>
                                        <div className="mentor-course text-center">
                                            <h3 className="mb-2">
                                                Verify Your <br />
                                                Email Address
                                            </h3>
                                            <p>
                                                Enter the 4-digit code we sent to your inbox to confirm
                                                it's really you.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <div className="login-carousel-section mb-3">
                                        <div className="login-banner">
                                            <ImageWithBasePath
                                                src="assets/img/index-3/banner-01.png"
                                                className="img-fluid"
                                                alt="Logo"
                                            />
                                        </div>
                                        <div className="mentor-course text-center">
                                            <h3 className="mb-2">
                                                One Step <br />
                                                Closer
                                            </h3>
                                            <p>
                                                Verifying your email helps us keep your account secure
                                                and ensures you never miss an important update.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <div className="login-carousel-section mb-3">
                                        <div className="login-banner">
                                            <ImageWithBasePath
                                                src="assets/img/index-3/register-img.png"
                                                className="img-fluid"
                                                alt="Logo"
                                            />
                                        </div>
                                        <div className="mentor-course text-center">
                                            <h3 className="mb-2">
                                                Didn't Get <br />
                                                the Code?
                                            </h3>
                                            <p>
                                                Check your spam folder, or request a new code once the
                                                timer runs out.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </Slider>
                        </div>
                        {/* /Login Banner */}
                        <div className="col-md-6 login-wrap-bg">
                            {/* Login */}
                            <div className="login-wrapper">
                                <div className="loginbox">
                                    <div className="w-100">
                                        <div className="d-flex align-items-center justify-content-between login-header">
                                            <ImageWithBasePath
                                                src="assets/img/logo-white.png"
                                                className="img-fluid"
                                                alt="Logo"
                                            />
                                            <Link to={route.homesix} className="link-1">
                                                Back to Home
                                            </Link>
                                        </div>
                                        <div className="topic">
                                            <h1 className="fs-32 fw-bold mb-3">Email OTP</h1>
                                            <p className="fs-14 fw-normal mb-0">
                                                OTP sent to your Email Address&nbsp;
                                                <strong>{maskEmail(email)}</strong>
                                            </p>
                                        </div>
                                        <form onSubmit={handleVerify} className="mb-3 pb-3">
                                            <div className="d-flex align-items-center mb-3">
                                                <Input.OTP
                                                    length={4}
                                                    value={otp}
                                                    status={otpInvalid ? "error" : undefined}
                                                    onChange={(val) => {
                                                        setOtp(val);
                                                        if (otpInvalid) setOtpInvalid(false);
                                                    }}
                                                />
                                            </div>
                                            {error && (
                                                <div className="text-danger fs-14 mb-3">{error}</div>
                                            )}
                                            {successMessage && (
                                                <div className="text-success fs-14 mb-3">{successMessage}</div>
                                            )}
                                            <div className="timer-cover d-flex align-items-center justify-content-center mb-3">
                                                <div className="badge badge-soft-danger rounded-pill d-flex align-items-center">
                                                    <i className="isax isax-clock me-1" />
                                                    <span id="otp_timer">
                                                        {timerActive ? `00:${formatTime(seconds)}` : "Expired"}
                                                    </span>
                                                    {timerActive && <span className="ms-1">s</span>}
                                                </div>
                                            </div>
                                            <div className="d-grid">
                                                <button
                                                    className="btn btn-secondary btn-lg"
                                                    type="submit"
                                                    disabled={loading}
                                                >
                                                    Verify &amp; Proceed
                                                    <i className="isax isax-arrow-right-3 ms-1" />
                                                </button>
                                            </div>
                                        </form>
                                        <div className="fs-14 fw-normal d-flex align-items-center justify-content-center">
                                            Didn't get the OTP?
                                            {seconds <= 0 ? (
                                                <button
                                                    type="button"
                                                    className="btn btn-link link-2 ms-1 p-0 fs-14"
                                                    onClick={handleResend}
                                                    disabled={loading}
                                                >
                                                    Resend OTP
                                                </button>
                                            ) : (
                                                <span className="text-muted ms-1">
                                                    Resend OTP (available in {seconds}s)
                                                </span>
                                            )}
                                        </div>
                                        {/* /Login */}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {/* /Main Wrapper */}
        </>
    );
};

export default Otp;
